// STRUCTURE-CHANGED (docs/design/2026-10-30-shared-des-and-fulfillment.md §3.1): the manual
// "Reports to…" picker. One small modal with a searchable seat list, used from the seat drawer,
// the chart table's row menu and a DE's page. Picking a seat never writes: it stages "move this
// seat under X" in the chart's draft, and "Review and apply" (simulate, then PUT edges) is where
// the server judges it (the reporting chain rule, 422 supervisor_required; cycles).
import {
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { CheckIcon, PencilIcon } from "lucide-react";
import { useState } from "react";
import type { Api } from "../../api/client";
import { keepFocus } from "../../focus/focus";
import type { CompanyAccess } from "../company";
import {
  type ChartSeat,
  type DraftOp,
  descendants,
  type OrgChart,
  supervisors,
} from "./chart-model";
import { seatNameText } from "./chart-parts";
import { orgChartQuery } from "./chart-queries";

/** The seats `seatId` may report to: not itself, not a seat under it (a cycle), not closed. */
export function reportsToChoices(
  chart: OrgChart,
  ops: readonly DraftOp[],
  seatId: string,
  query = "",
): ChartSeat[] {
  const below = descendants(seatId, supervisors(chart, ops));
  const q = query.trim().toLowerCase();
  return chart.seats
    .filter((s) => s.id !== seatId && s.status !== "closed" && !below.has(s.id))
    .filter(
      (s) => !q || s.title.toLowerCase().includes(q) || seatNameText(s).toLowerCase().includes(q),
    )
    .sort((a, b) => a.title.localeCompare(b.title));
}

/**
 * A DE's page: "Reports to…" for the DE's seat. The chart's draft lives on the chart, so the pick
 * opens the chart with the move staged (`?move=&under=`) and its review open.
 */
export function SeatReportsToButton({
  api,
  company,
  seatId,
}: {
  api: Api;
  company: CompanyAccess;
  seatId: string;
}) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const chart = useQuery({ ...orgChartQuery(api, company.id), enabled: open });
  return (
    <>
      <Button
        variant="ghost"
        size="icon-xs"
        aria-label="Reports to…"
        title="Reports to…"
        className="text-faint hover:text-foreground"
        data-slot="de-reports-to-edit"
        onClick={() => setOpen(true)}
      >
        <PencilIcon aria-hidden="true" />
      </Button>
      {open && chart.data ? (
        <ReportsToDialog
          chart={chart.data}
          ops={[]}
          seatId={seatId}
          onClose={() => setOpen(false)}
          onPick={(to) => {
            setOpen(false);
            void navigate({
              to: "/c/$slug/kpis/chart",
              params: { slug: company.slug },
              search: (prev: Record<string, unknown>) => ({
                ...keepFocus(prev),
                seat: seatId,
                move: seatId,
                under: to ?? "top",
              }),
            } as never);
          }}
        />
      ) : null}
    </>
  );
}

export function ReportsToDialog({
  chart,
  ops,
  seatId,
  onPick,
  onClose,
}: {
  chart: OrgChart;
  ops: readonly DraftOp[];
  seatId: string;
  /** The new supervisor (null: the top of the chart). */
  onPick: (to: string | null) => void;
  onClose: () => void;
}) {
  const [query, setQuery] = useState("");
  const seat = chart.seats.find((s) => s.id === seatId);
  const current = supervisors(chart, ops).get(seatId) ?? null;
  const choices = reportsToChoices(chart, ops, seatId, query);
  const top = !query.trim() || "top of the chart nobody".includes(query.trim().toLowerCase());
  const row = (key: string, to: string | null, title: string, name: string | null) => (
    <li key={key}>
      <button
        type="button"
        data-slot="reports-to-choice"
        data-seat={to ?? "top"}
        disabled={to === current}
        onClick={() => onPick(to)}
        className={cn(
          "flex w-full min-w-0 items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm",
          "hover:bg-muted focus-visible:outline-2 focus-visible:outline-ring disabled:opacity-60",
        )}
      >
        <span className="min-w-0 flex-1 truncate font-medium">{title}</span>
        {name ? <span className="shrink-0 truncate text-xs text-faint">{name}</span> : null}
        {to === current ? (
          <CheckIcon aria-label="Now" className="size-3.5 shrink-0 text-faint" />
        ) : null}
      </button>
    </li>
  );
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[440px]" data-slot="reports-to-dialog">
        <DialogHeader>
          <DialogTitle>{seat ? `${seat.title} reports to…` : "Reports to…"}</DialogTitle>
          <DialogDescription>Staged in the chart. Review and apply to save.</DialogDescription>
        </DialogHeader>
        <Input
          autoFocus
          aria-label="Find a seat"
          placeholder="Find a seat or person"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <ul className="-mx-2 flex max-h-72 flex-col overflow-y-auto" aria-label="Seats">
          {top ? row("top", null, "Nobody (top of the chart)", null) : null}
          {choices.map((s) => row(s.id, s.id, s.title, seatNameText(s)))}
          {!choices.length && !top ? (
            <li className="px-2 py-1.5 text-sm text-muted-foreground">No seat matches.</li>
          ) : null}
        </ul>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
